/**
 * Cached current-user profile (`GET /auth/me`).
 *
 * Previously every `PageHeader` (home variant) mount and the Settings page each
 * issued their own `/auth/me`, so navigating around re-fetched the profile
 * repeatedly. This store dedupes concurrent loads, caches the result for a
 * short TTL, and lets writers (Settings "save name") update the cache in place.
 */

import { writable } from 'svelte/store';
import { api } from '$lib/api.js';

export interface UserProfile {
	id: string;
	email: string;
	displayName: string | null;
	akahuFirstName: string | null;
	akahuLastName: string | null;
}

export const profile = writable<UserProfile | null>(null);

let inflight: Promise<UserProfile | null> | null = null;
let lastLoaded = 0;
const PROFILE_TTL_MS = 60_000;

/**
 * Load the profile, deduped and cached. Concurrent callers share one request;
 * a recent successful load (< `PROFILE_TTL_MS`) returns the cached value without
 * a network call. Pass `force: true` to always re-fetch (e.g. the Settings page,
 * which must show the freshest editable value).
 */
export async function loadProfile(force = false): Promise<UserProfile | null> {
	if (inflight) return inflight;

	if (!force && Date.now() - lastLoaded < PROFILE_TTL_MS) {
		let cached: UserProfile | null = null;
		profile.subscribe((p) => { cached = p; })();
		if (cached) return cached;
	}

	inflight = (async () => {
		try {
			const me = await api.auth.me();
			profile.set(me);
			lastLoaded = Date.now();
			return me;
		} catch {
			return null;
		} finally {
			inflight = null;
		}
	})();

	return inflight;
}

/** Update the cached profile in place (e.g. after Settings saves a new name). */
export function setProfile(next: UserProfile): void {
	profile.set(next);
	lastLoaded = Date.now();
}

/** Clear the cached profile on logout. */
export function resetProfile(): void {
	lastLoaded = 0;
	profile.set(null);
}
